/**
 * Feature-detecting bridge to the Tauri desktop shell.
 *
 * Every function here checks whether the app is actually running inside
 * Tauri (`window.__TAURI__` is only present there, never in a plain
 * browser tab) and falls back to equivalent web behavior otherwise, so
 * every page can call these unconditionally — the same React code runs
 * unmodified in the desktop app and in a normal browser.
 */

declare global {
  interface Window {
    __TAURI__?: unknown;
  }
}

export function isDesktopApp(): boolean {
  return typeof window !== "undefined" && window.__TAURI__ !== undefined;
}

type TauriInvoke = <T = unknown>(cmd: string, args?: Record<string, unknown>) => Promise<T>;

/**
 * Load the real Tauri `invoke` function, without TypeScript ever needing to
 * resolve `@tauri-apps/api`'s own type declarations.
 *
 * A dynamic `import()` with a LITERAL string still gets type-checked against
 * the actual package at compile time — which is exactly what broke the pure
 * web build (Cloudflare has no reason to install or type a desktop-only
 * package). Importing via a variable instead is standard TypeScript
 * behavior for conditional/optional dependencies: with a non-literal
 * specifier, TS can't statically know what it resolves to, so it types the
 * result as `any` and never tries to resolve the package's types. This
 * function only actually runs inside the real Tauri shell (every caller
 * below checks `isDesktopApp()` first), so the package only needs to exist
 * there, never in a browser/web build.
 */
async function getTauriInvoke(): Promise<TauriInvoke> {
  const tauriCoreModuleId = "@tauri-apps/api/core";
  const mod = await import(/* @vite-ignore */ tauriCoreModuleId);
  return mod.invoke as TauriInvoke;
}

/**
 * Show a notification: a native OS notification inside the desktop app,
 * or the browser Notification API (if permitted) otherwise.
 */
export async function showNotification(title: string, body: string): Promise<void> {
  if (isDesktopApp()) {
    const invoke = await getTauriInvoke();
    await invoke("show_native_notification", { title, body });
    return;
  }

  if (typeof Notification !== "undefined") {
    if (Notification.permission === "granted") {
      new Notification(title, { body });
    } else if (Notification.permission !== "denied") {
      const permission = await Notification.requestPermission();
      if (permission === "granted") new Notification(title, { body });
    }
  }
}

/**
 * Prompt the user to pick a file: a native OS file dialog in the desktop
 * app, or null (callers should render an `<input type="file">` instead)
 * in a plain browser.
 */
export async function pickFileNative(filterExtensions: string[]): Promise<string | null> {
  if (!isDesktopApp()) return null;
  const invoke = await getTauriInvoke();
  const path = await invoke<string | null>("pick_file", { filterExtensions });
  return path ?? null;
}

/**
 * Prompt the user to choose a save destination: a native OS save dialog
 * in the desktop app, or null (callers should trigger a normal browser
 * download instead) in a plain browser.
 */
export async function saveFileNative(defaultFileName: string): Promise<string | null> {
  if (!isDesktopApp()) return null;
  const invoke = await getTauriInvoke();
  const path = await invoke<string | null>("save_file", { defaultFileName });
  return path ?? null;
}
